<template>
	<div class="page-wrapper">
		<div class="page page-settings">
			<div class="container">
				<div class="page-header">
					<h1 class="page-title">
						<router-link class="text" to="/settings">{{ $t('Settings') }}</router-link>
						<i class="separator material-icons">chevron_right</i>
						<span class="page-breadcrumb">{{ title }}</span>
					</h1>
				</div>
				<div class="page-content">
					<!-- Main Content -->
					<div class="" v-if="user">
						<!-- Holiday Mode -->
						<div class="integration-item box p-0 hidden">
							<div class="integration-app">
								<div class="wrapper">
									<div class="integration-app-logo">
										<div class="text">{{ $t('Mode Libur') }}</div>
									</div>
								</div>
							</div>
							<div class="integration-accounts">
								<div class="integration-account">
									<div class="wrapper">
										<div class="container-fluid p-0">
											<div class="row">
												<div class="col-sm-12">
													<div class="form-group">
														<div class="d-flex justify-content-between">
															<div class="d-flex align-items-center">
																<label><b>Fitur Toko Libur</b></label>
															</div>
															<smart-toggle
																:no-padding-right="true"
																v-model="isHolidayMode"
																@input="onChangeStoreStatus"
															/>
														</div>
														<p class="mt-2">
															Aktifkan Fitur Toko Libur agar Seller tidak dapat membuat pesanan. Pesanan yang ada tetap harus diselesaikan.
														</p>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Store Information -->
						<div class="integration-item box p-0" id="information">
							<div class="integration-app">
								<div class="wrapper">
									<div class="integration-app-logo">
										<div class="text">{{ $t('Store Information') }}</div>
									</div>
								</div>
							</div>
							<div class="integration-accounts">
								<div class="integration-account">
									<div class="wrapper">
										<div class="container-fluid p-0">
											<div class="row">
												<div class="col-sm-4">
													<div class="form-group">
														<label><b>Store Logo</b></label>
														<div class="store-logo" :class="[{ 'is-loading': isUploadingLogo }]">
															<input type="file" accept="image/*" class="input-file" id="store-logo-file" @change="onStoreLogoChange">
															<label :for="`store-logo-file`" class="store-logo-upload" :class="[{ 'active': storeEdit.logo === '' }]">
																<div class="store-logo-upload-button" v-if="storeEdit.logo === ''"><i class="icon material-icons">camera_alt</i></div>
																<img :src="storeEdit.logo" class="store-logo-image" v-if="storeEdit.logo !== ''">
																<i class="icon material-icons" v-if="storeEdit.logo !== ''">camera_alt</i>
															</label>
														</div>
													</div>
													<button
														type="button"
														class="btn btn-light btn-sm"
														@click="removeLogo"
														v-if="storeEdit.logo !== ''">
														Remove Logo
													</button>
												</div>
												<div class="col-sm-8">
													<!-- Store Name -->
													<div class="form-group">
														<label><b>Store Name</b></label>
														<input type="text" class="input form-control" placeholder="Your Store Name.." :class="[{ 'error': !isStoreNameValid }]" v-model="storeEdit.name">
														<small class="text-danger" v-if="!isStoreNameValid">Store Name cannot be empty</small>
													</div>
													<!-- Store Phone -->
													<div class="form-group">
														<label><b>Store Phone</b></label>
														<input type="text" class="input form-control" placeholder="Your Store Phone Number.." v-model="storeEdit.phone">
													</div>
													<!-- Store Email -->
													<div class="form-group">
														<label><b>Store Email</b></label>
														<input type="text" class="input form-control" placeholder="Your Store Email.." v-model="storeEdit.email">
													</div>
													<!-- Store Website -->
													<div class="form-group">
														<label><b>Store Website</b></label>
														<input type="text" class="input form-control" placeholder="Your Store Website.." v-model="storeEdit.website">
													</div>
													<!-- Store City -->
													<div class="form-group">
														<label><b>Store City</b></label>
														<multiselect v-model="selectedStoreSubdistrict" :custom-label="cityLabel"
															:show-labels="false" track-by="subdistrict_id"
															placeholder="Enter the City / District (minimum 3 letters)"
															:options="storeSubdistricts"
															@search-change="fetchStoreSubdistricts">
														</multiselect>
													</div>
													<!-- Store Address -->
													<div class="form-group">
														<label><b>Store Address</b></label>
														<input type="text" class="input form-control" placeholder="Your Store Address.." v-model="storeEdit.address">
													</div>

													<div class="form-group">
														<label><b>Store Description</b></label>
														<!-- <textarea class="input form-control" placeholder="Your Store Description (about us)" v-model="storeEdit.about"></textarea> -->
														<content-editor
															class="mt-2"
															v-model="storeEdit.about">
														</content-editor>
													</div>
												</div>
												<div class="col-12">
													<div class="form-group text-right">
														<button type="button"
															class="btn btn-primary"
															:class="{ 'is-loading': isSavingStoreInformation }"
															:disabled="!isStoreValid"
															@click="saveStoreInformation">
															<i class="material-icons">save</i><span class="va-m">Update Store Information</span>
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Shipping -->
						<div class="integration-item box p-0" id="shipping">
							<div class="integration-app">
								<div class="wrapper">
									<div class="integration-app-logo">
										<div class="text">{{ $t('Default Shipping') }}</div>
									</div>
								</div>
							</div>
							<div class="integration-accounts">
								<div class="integration-account">
									<div class="wrapper">
										<div class="container-fluid p-0">
											<div class="row">
												<!-- Origin -->
												<div class="col-sm-6">
													<div class="form-group mb-5">
														<label class="mb-2"><b>Default Shipping Origin</b></label>
														<!-- Province -->
														<div class="form-group ship-from-field">
															<label>{{ $t('_.province') }}</label>
															<multiselect
																placeholder="Select Province"
																label="province_name"
																track-by="province_id"
																v-model="shippingProvince"
																:options="provinces"
																:show-labels="false"
																:allow-empty="false"
																:loading="isFetchingProvinces"
																:preselect-first="true"
																:disabled="provinces.length === 0"
																@select="onProvinceChange">
															</multiselect>
														</div>

														<!-- City -->
														<div class="form-group ship-from-field">
															<label>{{ $t('_.city') }}</label>
															<multiselect
																placeholder="Select City"
																label="city_name_with_type"
																track-by="city_id"
																v-model="shippingCity"
																:options="cities"
																:show-labels="false"
																:allow-empty="false"
																:loading="isFetchingCities"
																:preselect-first="true"
																:disabled="cities.length === 0"
																@select="onCityChange">
															</multiselect>
														</div>

														<!-- Subdistrict -->
														<div class="form-group ship-from-field">
															<label>{{ $t('_.subdistrict') }}</label>
															<multiselect
																placeholder="Select Subdistrict"
																label="subdistrict_name"
																track-by="subdistrict_id"
																v-model="shippingSubdistrict"
																:options="subdistricts"
																:show-labels="false"
																:allow-empty="false"
																:loading="isFetchingSubdistricts"
																:preselect-first="false"
																:disabled="subdistricts.length === 0">
															</multiselect>
														</div>

														<!-- ZIP Code -->
														<div class="form-group ship-from-field">
															<label>ZIP Code</label>
															<input
																class="form-control"
																placeholder="ZIP Code"
																v-model="shippingZip">
														</div>

														<!-- Phone -->
														<div class="form-group ship-from-field">
															<label>Phone PIC Pickup</label>
															<input
																type="text"
																class="form-control"
																v-model="phone1"
															/>
														</div>

														<!-- Phone -->
														<div class="form-group ship-from-field">
															<label>Phone PIC Support</label>
															<input
																type="text"
																class="form-control"
																v-model="phone2"
															/>
														</div>

														<!-- Address -->
														<div class="form-group ship-from-field">
															<label>Address</label>
															<textarea
																class="form-control"
																rows="5"
																v-model="address"
															></textarea>
														</div>
													</div>
												</div>

												<!-- COD Settings -->
												<div class="col-sm-6 p-0">
													<!-- <cod-settings
														class="mb-3"
														:position="'global'"
														:couriers.sync="codCouriers"
														:smart-couriers.sync="smartCodCouriers"
														:is-smart.sync="shippingSmartMode"
														v-model="cod"
													/> -->
												</div>

												<!-- Default Shipping Couriers -->
												<div class="col-sm-6 mt-2">
													<!-- Shipping Smart Mode -->
													<!-- <div
														class="form-group"
														v-if="isAdmin || isHasFeature('smart_shipping')">
														<div class="mb-2"><b>Smart Mode</b></div>
														<checkbox-toggle
															class="d-inline-block mb-4"
															label="Activate Smart Mode"
															:info="$t('Sistem Ostock akan memilihkan kurir dengan performa terbaik')"
															v-model="shippingSmartMode"
														/>
													</div> -->

													<div class="form-group">
														<label class="mb-3">
															<b>Default Shipping Couriers</b>
														</label>
														<div class="box__content">
															<!-- Couriers -->
															<div class="form-group">
																<div class="shipping-couriers">
																	<div class="row">
																		<template v-for="(courier, index) in shippingCouriers">
																			<div class="col-6" :key="index">
																				<label>
																					<input type="checkbox" class="input-checkbox" :value="courier.key" v-model="storeEdit.shipping.couriers" :disabled="courier.force_enabled">
																					<span></span>
																					<span class="input-checkbox-text shipping-logo" :style="{ backgroundImage: `url('/images/logo-${courier.key}.png')`}" :title="courier.label">
																					</span>
																				</label>
																			</div>
																		</template>
																	</div>
																</div>
																<small class="text-danger" v-if="!isStoreCouriersValid">Must choose at least one courier</small>
															</div>
														</div>
													</div>
												</div>

												<!-- Shipping Services -->
												<div class="col-sm-6 mt-2 hidden">
													<div class="shipping-services form-group">
														<label><b>{{ $t('Shipping Services') }}:</b></label>
														<div class="container-fluid">
															<div class="row">
																<div
																	class="col-lg-4 p-0"
																	v-for="(service, index) in shippingServices"
																	:key="index">
																	<div class="form-group mb-1">
																		<label>
																			<input
																				type="checkbox"
																				class="input-checkbox input-checkbox-large"
																				:value="service.value"
																				v-model="services"
																				:disabled="services.length === 1 && services.indexOf(service.value) !== -1">
																			<span></span>
																			<span class="input-checkbox-text">{{service.label}}</span>
																		</label>
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>

												<!-- apply to all toggle -->
												<div class="col-12">
													<div class="form-group">
														<smart-toggle
															v-model="applyToAll"
															label="Do you want to apply default shipping to all products?"
															@input="onChange"
														/>
													</div>
												</div>

												<div class="col-12">
													<div class="form-group text-right">
														<button
															type="button"
															class="btn btn-primary btn-icon"
															:class="{ 'is-loading': isSavingStoreInformation }"
															:disabled="!isStoreValid"
															@click="confirmUpdateShipping">
															<i class="material-icons">save</i>
															<span>Update Shipping</span>
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Warehouse -->
						<div class="integration-item box p-0 hidden" id="warehouse">
							<div class="integration-app">
								<div class="wrapper">
									<div class="integration-app-logo">
										<div class="text">{{ $t('Warehouse') }}</div>
									</div>
									<button type="button" class="integration-app-button btn btn-primary" @click="openFormWarehouse('add')">+ <span class="ml-2">{{ $t('Add Warehouse') }}</span></button>
								</div>
							</div>
							<div class="">
								<template v-if="listWarehouses && listWarehouses.length > 0">
									<div
										class="integration-accounts"
										v-for="(item, index) in listWarehouses"
										:key="index">
										<div class="integration-account border-bottom">
											<div class="wrapper">
												<div class="integration-account__name">
													<div class="d-flex align-items-center">
														<div class="mr-4">
															{{ index + 1 }}.
														</div>
														<div class="d-flex flex-column">
															<b>{{ item.label }}</b>
															<div class="mt-1">
																{{ item.address }}, <span class="ml-1" v-if="item.subdistrict && item.subdistrict !== null">{{ item.subdistrict.name }}</span>, <span class="mx-2" v-if="item.city && item.city !== null">{{ item.city.name }}</span>, <span v-if="item.province && item.province !== null">{{ item.province.name }}</span>
															</div>
														</div>
													</div>
												</div>
												<div class="integration-account__actions">
													<button
														type="button"
														class="integration-account__edit btn-plain d-none"
														v-tooltip="item.default ? 'Default' : 'Set As Default'"
														@click="setWarehouseDefault(item)">
														<i
															class="material-icons"
															:class="{
																'text-warning': item.default,
																'text-gray': !item.default
															}"
															>star</i>
													</button>
													<button type="button" class="integration-account__edit btn-plain" v-tooltip="'Edit'" @click="openFormWarehouse('edit', item)">
														<i class="material-icons">edit</i>
													</button>
													<button type="button" class="integration-account__remove btn-plain btn-icon-danger" v-tooltip="'Delete'" @click="deleteWarehouse(item)">
														<i class="material-icons">close</i>
													</button>
												</div>
											</div>
										</div>
									</div>
								</template>

								<!-- empty warehouses -->
								<template v-else>
									<div class="p-4 text-center" v-if="isFetchingWarehouse">
										<div class="is-loading dark"></div>
									</div>
									<div class="integration-accounts" v-if="!isFetchingWarehouse">
										<div class="integration-account border-bottom">
											<div class="wrapper">
												<div class="integration-account__name">
													<div class="d-flex align-items-center">
														No Data
													</div>
												</div>
											</div>
										</div>
									</div>
								</template>

							</div>
						</div>

						<!-- Bank Accounts -->
						<div class="integration-item box p-0 hidden">
							<div class="integration-app">
								<div class="wrapper">
									<div class="integration-app-logo">
										<div class="text">{{ $t('Bank Accounts') }}</div>
									</div>
									<button type="button" class="integration-app-button btn btn-primary" @click="isShowAddBank = true">+ <span class="ml-2">{{ $t('Add Bank Account') }}</span></button>
								</div>
							</div>
							<bank-accounts :accounts="bankAccounts"></bank-accounts>
						</div>

						<!-- Social Media Accounts -->
						<div class="integration-item box p-0 hidden">
							<div class="integration-app">
								<div class="wrapper">
									<div class="integration-app-logo">
										<div class="text">{{ $t('Social Media Accounts') }}</div>
									</div>
								</div>
							</div>
							<div class="integration-accounts">
								<div class="integration-account">
									<div class="wrapper">
										<div class="container-fluid p-0">
											<div class="row">
												<div class="col-sm-12">
													<div class="d-flex mb-3">
														<div class="integration-account__icon-small mr-3">
															<img class="img-responsive" src="/images/logo-facebook.png" alt="instagram">
														</div>
														<input type="text" class="input form-control input-medium" placeholder="eg: https://facebook.com/ostock.id" v-model="storeEdit.social_media_accounts.facebook">
													</div>
													<div class="d-flex mb-3">
														<div class="integration-account__icon-small mr-3 float-left">
															<img class="img-responsive" src="/images/logo-instagram.png" alt="instagram">
														</div>
														<input type="text" class="input form-control input-medium" placeholder="eg: https://instagram.com/orderonlineid" v-model="storeEdit.social_media_accounts.instagram">
													</div>
													<div class="d-flex mb-3">
														<div class="integration-account__icon-small mr-3 float-left">
															<img class="img-responsive" src="/images/logo-youtube.png" alt="instagram">
														</div>
														<input type="text" class="input form-control input-medium" placeholder="eg: https://youtube.com/c/orderonline" v-model="storeEdit.social_media_accounts.youtube">
													</div>
												</div>
												<div class="col-12">
													<div class="form-group text-right">
														<button
															type="button"
															class="btn btn-primary btn-icon"
															@click="saveStoreInformation">
															<i class="material-icons">save</i>
															<span>Update Social Media Account</span>
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Location -->
						<div class="integration-item box p-0" id="location">
							<div class="integration-app">
								<div class="wrapper">
									<div class="integration-app-logo">
										<div class="text">{{ $t('Store Location') }}</div>
									</div>
								</div>
							</div>
							<div class="integration-accounts">
								<div class="integration-account">
									<div class="wrapper">
										<div class="container-fluid p-0">
											<div class="row">
												<div class="col-sm-6">
													<div class="form-group">
														<div class="form-group">
															<label><b>Warehouse Photo</b></label>
															<div class="store-photo" :class="[{ 'is-loading': isUploadingWarehousePhoto }]">
																<input type="file" accept="image/*" class="input-file" id="store-photo-file" @change="onWarehousePhotoChange">
																<label :for="`store-photo-file`" class="store-logo-upload" :class="[{ 'active': storeEdit.warehouse_photo === '' }]">
																	<div class="store-logo-upload-button" v-if="storeEdit.warehouse_photo === ''"><i class="icon material-icons">camera_alt</i></div>
																	<img :src="storeEdit.warehouse_photo" class="store-logo-image" v-if="storeEdit.warehouse_photo !== ''">
																	<i class="icon material-icons" v-if="storeEdit.warehouse_photo !== ''">camera_alt</i>
																</label>
															</div>
														</div>
														<button
															type="button"
															class="btn btn-light btn-sm"
															@click="removeWarehousePhoto"
															v-if="storeEdit.warehouse_photo !== ''">
															Remove Photo
														</button>
													</div>
												</div>
												<div class="col-sm-6">
													<div class="form-group">
														<div class="form-group">
															<label><b>Warehouse Photo With Owner</b></label>
															<div class="store-photo" :class="[{ 'is-loading': isUploadingWarehousePhotoWithOwner }]">
																<input type="file" accept="image/*" class="input-file" id="store-photo-owner-file" @change="onWarehousePhotoWithOwnerChange">
																<label :for="`store-photo-owner-file`" class="store-logo-upload" :class="[{ 'active': storeEdit.warehouse_photo_with_owner === '' }]">
																	<div class="store-logo-upload-button" v-if="storeEdit.warehouse_photo_with_owner === ''"><i class="icon material-icons">camera_alt</i></div>
																	<img :src="storeEdit.warehouse_photo_with_owner" class="store-logo-image" v-if="storeEdit.warehouse_photo_with_owner !== ''">
																	<i class="icon material-icons" v-if="storeEdit.warehouse_photo_with_owner !== ''">camera_alt</i>
																</label>
															</div>
														</div>
														<button
															type="button"
															class="btn btn-light btn-sm"
															@click="removeWarehousePhotoWithOwner"
															v-if="storeEdit.warehouse_photo_with_owner !== ''">
															Remove Photo
														</button>
													</div>
												</div>
												<div class="col-sm-4">
													<div class="form-group">
														<label><b>Maps Latitude</b></label>
														<input type="text" class="input form-control" placeholder="Your Store latitude coordinate. eg: 51.508742" v-model.number="storeEdit.location.lat">
													</div>
													<div class="form-group">
														<label><b>Maps Longitude</b></label>
														<input type="text" class="input form-control" placeholder="Your Store longitude coordinate. eg: -0.120850" v-model.number="storeEdit.location.lng">
													</div>
												</div>
												<div class="col-sm-8">
													<leaflet-map
														@set-location="setLocation"
														v-bind:lat.sync="storeEdit.location.lat"
														v-bind:lng.sync="storeEdit.location.lng"
													/>
												</div>
												<div class="col-12 mt-4">
													<div class="form-group text-right">
														<button
															type="button"
															class="btn btn-primary btn-icon"
															:class="{ 'is-loading': isSavingStoreInformation }"
															@click="saveStoreInformation">
															<i class="material-icons">save</i>
															<span>Update Store Location</span>
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<!-- Loader -->
					<div class="is-loading large mt-3" v-if="!user"></div>
				</div>
			</div>
		</div>

		<div class="modals">
			<!-- Bank -->
			<bank-accounts-modal
				:show="isShowAddBank"
				@close="isShowAddBank = false">
			</bank-accounts-modal>

			<!-- warehouse -->
			<warehouse-form
				:show="isShowFormWarehouse"
				:formType="warehouseFormType"
				:selectedWarehouse="selectedWarehouse"
				@set="setWarehouse"
				@close="isShowFormWarehouse = false"
			/>
		</div>

		<!-- Modal Confirm Apply to All -->
		<modal
			class="table-filter-modal"
			size="medium"
			@close="isShowConfirmApply = false"
			v-if="isShowConfirmApply">
			<div slot="header">
				<h3>Confirm Apply to All</h3>
			</div>
			<div slot="body">
				Apakah Anda yakin akan menerapkan <b>Default Shipping</b> untuk semua produk? <b>Alamat</b> dan <b>Kurir</b> akan diterapkan ke semua produk.
			</div>
			<div slot="footer">
				<button type="button" class="btn btn-light" @click="isShowConfirmApply = false">{{ $t('Close') }}</button>
				<button type="button" class="btn btn-primary" @click="saveStoreInformation">Submit</button>
			</div>
		</modal>

		<!-- Modal Store Status -->
		<modal-update-store-status
			v-if="isStoreStatusModalVisible"
			:store-status-state="storeStatusState"
			:is-updating-store-status="isUpdatingStoreStatus"
			@close="toggleStoreStatusModal(false, true)"
			@update="updateStoreStatus"
		/>

		<notifications group="store" position="bottom right" />
	</div>
</template>

<script>
/* eslint-disable */
import { mapGetters, mapActions } from 'vuex';
import Modal from '@/components/Modal.vue';
import IntegrationAccounts from '@/components/IntegrationAccounts.vue';
import BankAccounts from '@/components//BankAccounts.vue';
import BankAccountsModal from '@/components/BankAccountsModal.vue';
import GoogleMap from '@/components//GoogleMap.vue';
import LeafletMap from '@/components/LeafletMap.vue';
import CodSettings from '@/components/CodSettings.vue';
import ContentEditor from '@/components/ContentEditor.vue';
import WarehouseForm from '@/components/WarehouseForm.vue';
import CheckboxToggle from '@/components/CheckboxToggle.vue';
import SmartToggle from '@/components/SmartToggle.vue';
import ModalUpdateStoreStatus from '@/components/ModalUpdateStoreStatus.vue';
import {
	banks,
	shippingCouriers,
	shippingServices,
	defaultCod,
} from '@/lib/statics';
import { duplicateVar, scrollTo } from '@/lib/helper';
// import integrationApi from '@/api/integration';
import mediaApi from '@/api/media';
import shippingApi from '@/api/shipping';
import warehouseApi from '@/api/warehouse';
import { updateStoreStatus } from '@/api/user';
import shipping from '@/api/shipping';

export default {
	name: 'settingsStore',
	components: {
		Modal,
		IntegrationAccounts,
		BankAccounts,
		BankAccountsModal,
		GoogleMap,
		LeafletMap,
		CodSettings,
		ContentEditor,
		WarehouseForm,
		CheckboxToggle,
		SmartToggle,
		ModalUpdateStoreStatus,
	},
	data() {
		return {
			title: 'Store',
			bank: null,
			accountName: '',
			accountNumber: '',
			banks,
			storeEdit: false,
			isFetching: false,
			isShowAddBank: false,
			isSavingBankAccount: false,
			isUploadingLogo: false,
			isUploadingWarehousePhoto: false,
			isUploadingWarehousePhotoWithOwner: false,
			isSavingStoreInformation: false,
			isFetchingProvinces: false,
			isFetchingCities: false,
			isFetchingSubdistricts: false,
			shippingCouriers,
			shippingServices,
			provinces: [],
			cities: [],
			subdistricts: [],
			shippingProvince: null,
			shippingCity: null,
			shippingSubdistrict: null,
			shippingZip: '',
			phone1: '',
			phone2: '',
			address: '',
			cod: defaultCod,
			codCouriers: [],
			smartCodCouriers: [],
			services: [],
			warehouses: [],
			isShowFormWarehouse: false,
			listWarehouses: [],
			warehouseFormType: 'add',
			selectedWarehouse: null,
			isFetchingWarehouse: false,
			shippingSmartMode: false,
			applyToAll: false,
			isShowConfirmApply: false,
			isHolidayMode: false,
			isStoreStatusModalVisible: false,
			isUpdatingStoreStatus: false,
			isLoadingStoreSubdistrics: false,
			storeSubdistrictKeyword: '',
			selectedStoreSubdistrict: null,
			storeSubdistricts: [],
		};
	},
	methods: {
		...mapActions({
			addBankAccount: 'addBankAccount',
			removeBankAccount: 'removeBankAccount',
			updateStore: 'updateStore',
			setStoreShippingProvince: 'setStoreShippingProvince',
			setStoreShippingCity: 'setStoreShippingCity',
		}),
		resetBankAccount() {
			this.accountName = '';
			this.accountNumber = '';
			this.isShowAddBank = false;
		},
		isBankAccountValid() {
			const isValid = this.accountName !== '' && this.accountNumber !== '';
			return isValid;
		},
		addNewBankAccount() {
			if (this.isBankAccountValid) {
				this.isSavingBankAccount = true;
				const bankAccount = {
					name: this.bank.name,
					label: this.bank.label,
					account_name: this.accountName,
					account_number: this.accountNumber,
				};
				const cb = () => {
					this.isSavingBankAccount = false;
					this.resetBankAccount();
				};
				const errorCb = () => {
					this.isSavingBankAccount = false;
					this.resetBankAccount();
				};
				this.addBankAccount({ bankAccount, cb, errorCb });
			}
		},
		removeTheBankAccount(index) {
			this.isSavingBankAccount = true;
			const cb = () => {
				this.isSavingBankAccount = false;
			};
			const errorCb = () => {
				this.isSavingBankAccount = false;
			};
			this.removeBankAccount({ index, cb, errorCb });
		},
		resetBank() {
			this.accountName = '';
			this.accountNumber = '';
		},
		onStoreLogoChange(event) {
			this.isUploadingLogo = true;
			const input = event.target;
			if (input.files && input.files[0]) {
				const image = input.files[0];

				// Preview
				// const reader = new FileReader();
				// reader.onload = (e) => {
				// 	this.image = e.target.result;
				// };
				// reader.readAsDataURL(image);

				// Start Upload
				const uploadParams = new FormData();
				uploadParams.append('image', image);

				const callback = (response) => {
					const path = response.data;
					const url = `${this.mediaUrlPrefix}/${path}`;
					this.storeEdit.logo = url;
					this.isUploadingLogo = false;
				};

				const errorCallback = (e) => {
					// eslint-disable-next-line
					console.log(e);
					this.isUploadingLogo = false;
				};

				// Upload Image
				mediaApi.uploadImage(uploadParams, callback, errorCallback);
			}
			// Reset file input
			event.target.value = null;
		},
		saveStoreInformation() {
			this.isShowConfirmApply = false;
			if (this.isStoreValid) {
				this.isSavingStoreInformation = true;
				const store = duplicateVar(this.storeEdit);
				const provinceId = this.shippingProvince ? this.shippingProvince.province_id : '';
				const provinceName = this.shippingProvince ? this.shippingProvince.province_name : '';
				const province = {
					id: provinceId,
					name: provinceName,
				};
				const cityId = this.shippingCity ? this.shippingCity.city_id : '';
				const cityName = this.shippingCity ? this.shippingCity.city_name_with_type : '';
				const city = {
					id: cityId,
					name: cityName,
				};
				const subdistrictId = this.shippingSubdistrict ? this.shippingSubdistrict.subdistrict_id : '';
				const subdistrictName = this.shippingSubdistrict ? this.shippingSubdistrict.subdistrict_name : '';
				const subdistrict = {
					id: subdistrictId,
					name: subdistrictName,
				};
				const zipCode = this.shippingZip.trim();
				const phone1 = this.phone1.trim();
				const phone2 = this.phone2.trim();
				const address = this.address.trim();

				store.shipping.origin.province = province;
				store.shipping.origin.city = city;
				store.shipping.origin.subdistrict = subdistrict;
				store.shipping.origin.zip = zipCode;
				store.shipping.origin.phone_1 = phone1;
				store.shipping.origin.phone_2 = phone2;
				store.shipping.origin.address = address;

				// Shipping Services
				store.shipping.services = this.services

				// COD
				store.shipping.cod = duplicateVar(this.cod);

				// COD Couriers
				store.shipping.cod_couriers = this.codCouriers;

				// Smart COD Couriers
				if (this.smartCodCouriers && this.smartCodCouriers.length > 0) {
					store.shipping.smart_cod_couriers = this.smartCodCouriers;
				}

				// Mode
				const shippingMode = this.shippingSmartMode ? 'smart' : 'normal';
				store.shipping.mode = shippingMode;

				store.availability = !this.isHolidayMode;

				if (this.applyToAll) {
					store.apply_to_all = this.applyToAll;
				}

				// store address detail
				if (this.selectedStoreSubdistrict) {
					store.address_detail = this.addressDetail;
				}

				const cb = () => {
					this.isSavingStoreInformation = false;
					this.$notify({
						group: 'store',
						title: 'Store',
						text: 'Information successfully updated',
						type: 'success',
					});
				};
				const errorCb = () => {
					this.isSavingStoreInformation = false;
				};
				this.updateStore({ store, cb, errorCb });
			}
		},
		fetchProvinces() {
			this.isFetchingProvinces = true;
			const callback = (response) => {
				this.provinces = response.data;
				this.isFetchingProvinces = false;
			};
			const errorCallback = (e) => {
				// eslint-disable-next-line
				console.log(e);
				this.isFetchingProvinces = false;
			};
			shippingApi.getAllProvinces(callback, errorCallback);
		},
		fetchCities(provinceId) {
			this.isFetchingCities = true;
			const params = {
				province_id: provinceId,
			};
			const callback = (response) => {
				this.isFetchingCities = false;
				this.cities = response.data;
			};
			const errorCallback = (e) => {
				this.isFetchingCities = false;
				// eslint-disable-next-line
				console.log(e);
			};
			shippingApi.getCities(params, callback, errorCallback);
		},
		fetchSubdistricts(cityId) {
			this.isFetchingSubdistricts = true;
			const callback = (response) => {
				this.subdistricts = response.data;
				this.isFetchingSubdistricts = false;
			};
			const errorCallback = () => {
				this.isFetchingSubdistricts = false;
			};
			shippingApi.getSubdistricts(cityId, callback, errorCallback);
		},
		onProvinceChange(province) {
			this.shippingCity = null;
			this.shippingSubdistrict = null;
			this.cities = [];
			this.subdistricts = [];

			const provinceId = province.province_id;
			this.fetchCities(provinceId);
		},
		onCityChange(city) {
			this.shippingSubdistrict = null;
			this.subdistricts = [];

			const cityId = city.city_id;
			this.fetchSubdistricts(cityId);
		},
		setDefaultProvince() {
			if (this.storeEdit) {
				const theProvince = this.storeEdit.shipping.origin.province;
				const selectedProvince = this.provinces.find(province => province.province_id === theProvince.id);
				if (selectedProvince) {
					this.shippingProvince = selectedProvince;
					this.fetchCities(selectedProvince.province_id);
				}
			}
		},
		setDefaultCity() {
			if (this.storeEdit) {
				const theCity = this.storeEdit.shipping.origin.city;
				const selectedCity = this.cities.find(city => city.city_id === theCity.id);
				if (selectedCity) {
					this.shippingCity = selectedCity;
					this.fetchSubdistricts(this.shippingCity.city_id);
				}
			}
		},
		setDefaultSubdistrict() {
			if (this.storeEdit) {
				const theSubdistrict = this.storeEdit.shipping.origin.subdistrict;
				const selectedSubdistrict = this.subdistricts.find(subdistrict => subdistrict.subdistrict_id === theSubdistrict.id);
				if (selectedSubdistrict) {
					this.shippingSubdistrict = selectedSubdistrict;
				}
			}
		},
		setStoreSettings() {
			this.storeEdit = duplicateVar(this.store);
			if (!this.storeEdit.social_media_accounts) {
				this.storeEdit.social_media_accounts = {
					youtube: '',
					instagram: '',
					facebook: '',
				};
			}
			if (!this.storeEdit.location) {
				this.storeEdit.location = {
					lat: '',
					lng: '',
				};
			}

			if (typeof this.storeEdit.warehouse_photo === 'undefined') {
				this.storeEdit.warehouse_photo = '';
			}

			if (typeof this.storeEdit.warehouse_photo_with_owner === 'undefined') {
				this.storeEdit.warehouse_photo_with_owner = '';
			}

			if (typeof this.storeEdit.availability === 'undefined') {
				this.storeEdit.availability = true;
			}

			this.isHolidayMode = !this.storeEdit.availability;

			if (!this.storeEdit.shipping.couriers || this.storeEdit.shipping.couriers.length === 0) {
				this.storeEdit.shipping.couriers = ['oexpress'];
			}

			const services = this.store.shipping.services ? this.store.shipping.services : ['regular'];
			this.services = services;

			if (this.store.shipping.cod) {
				const cod = duplicateVar(this.store.shipping.cod);
				this.cod = cod;
			}

			if (this.store.shipping.cod_couriers) {
				this.codCouriers = this.store.shipping.cod_couriers;
			} else if (!this.store.shipping.cod_couriers || this.store.shipping.cod_couriers.length === 0) {
				this.codCouriers = ['oexpress'];
			}

			this.smartCodCouriers = this?.store?.shipping?.smart_cod_couriers ?? [];

			if (this.storeEdit.shipping.origin.zip) {
				this.shippingZip = this.storeEdit.shipping.origin.zip;
			}

			if (this.storeEdit.shipping.origin.phone_1) {
				this.phone1 = this.storeEdit.shipping.origin.phone_1;
			}

			if (this.storeEdit.shipping.origin.phone_2) {
				this.phone2 = this.storeEdit.shipping.origin.phone_2;
			}

			if (this.storeEdit.shipping.origin.address) {
				this.address = this.storeEdit.shipping.origin.address;
			}

			if (this.storeEdit?.address_detail) {
				this.selectedStoreSubdistrict = this.storeEdit?.address_detail;
			}

			const shippingMode = this?.storeEdit?.shipping?.mode ?? 'normal';
			this.shippingSmartMode = shippingMode === 'smart';

			// set force enabled couriers to store settings
			const forcedCouriers = shippingCouriers.filter(
				({ force_enabled }) => !!force_enabled,
			);

			forcedCouriers.forEach((courier) => {
				const courierKey = courier.key;
				if (this.storeEdit.shipping.couriers.indexOf(courierKey) === -1) {
					this.storeEdit.shipping.couriers.push(courierKey);
				}
			});
		},
		checkHash() {
			setTimeout(() => {
				const section = window.location.hash.substr(1);
				scrollTo(section);
			}, 300);
		},
		removeLogo() {
			this.$set(this.storeEdit, 'logo', '');
		},
		removeWarehousePhoto() {
			this.$set(this.storeEdit, 'warehouse_photo', '');
			this.$forceUpdate();
		},
		removeWarehousePhotoWithOwner() {
			this.$set(this.storeEdit, 'warehouse_photo_with_owner', '');
			this.$forceUpdate();
		},
		fetchWarehouses() {
			this.isFetchingWarehouse = true;
			const params = {
				page: 1,
				limit: 100,
				is_deleted: false,
			};
			const callback = (response) => {
				const lists = response.data;
				this.listWarehouses = lists;
				this.isFetchingWarehouse = false;
			};
			const errorCallback = (e) => {
				// eslint-disable-next-line
				console.log(e);
				this.isFetchingWarehouse = false;
			};
			warehouseApi.getWarehouses(params, callback, errorCallback);
		},
		openFormWarehouse(type, warehouse = null) {
			this.warehouseFormType = type;
			this.isShowFormWarehouse = true;

			if (warehouse !== null) {
				this.selectedWarehouse = warehouse;
			}
		},
		deleteWarehouse(item) {
			const id = item._id;
			// eslint-disable-next-line
			const callback = (response) => {
				const index = this.listWarehouses.findIndex(curr => curr._id === item._id);
				if (index !== -1) {
					this.listWarehouses.splice(index, 1)
				}
			};
			const errorCallback = (e) => {
				// eslint-disable-next-line
				console.log(e);
			};
			warehouseApi.delete(id, callback, errorCallback);
		},
		setWarehouse(value) {
			if (this.warehouseFormType === 'add') {
				this.listWarehouses.push(value);
			} else if (this.warehouseFormType === 'edit') {
				const index = this.listWarehouses.findIndex(curr => curr._id === value._id);
				if (index !== -1) {
					this.listWarehouses[index].label = value.label;
					this.listWarehouses[index].province = value.province;
					this.listWarehouses[index].city = value.city;
					this.listWarehouses[index].subdistrict = value.subdistrict;
					this.listWarehouses[index].address = value.address;
					this.listWarehouses[index].default = value.default;
				}
			}
		},
		setWarehouseDefault(item) {
			// set only undefault to default
			if (!item.default) {
				const listWarehouses = this.listWarehouses.map(curr => {
					curr.default = false
					return curr
				});
				this.listWarehouses = listWarehouses.map(curr => {
					if (curr._id === item._id) {
						curr.default = true
					}
					return curr
				})
			}
		},
		onChange(value) {
			this.applyToAll = value;
		},
		confirmUpdateShipping() {
			if (this.applyToAll) {
				this.isShowConfirmApply = true;
				return;
			}
			this.saveStoreInformation();
		},
		setLocation(location) {
			this.storeEdit.location.lat = location?.lat || '';
			this.storeEdit.location.lng = location?.lng || '';
		},
		onWarehousePhotoChange(event) {
			this.isUploadingWarehousePhoto = true;
			const input = event.target;
			if (input.files && input.files[0]) {
				const image = input.files[0];

				// Start Upload
				const uploadParams = new FormData();
				uploadParams.append('image', image);

				const callback = (response) => {
					const path = response.data;
					const url = `${this.mediaUrlPrefix}/${path}`;
					this.storeEdit.warehouse_photo = url;
					this.isUploadingWarehousePhoto = false;
				};

				const errorCallback = (e) => {
					// eslint-disable-next-line
					console.log(e);
					this.isUploadingWarehousePhoto = false;
				};

				// Upload Image
				mediaApi.uploadImage(uploadParams, callback, errorCallback);
			}
			// Reset file input
			event.target.value = null;
		},
		onWarehousePhotoWithOwnerChange(event) {
			this.isUploadingWarehousePhotoWithOwner = true;
			const input = event.target;
			if (input.files && input.files[0]) {
				const image = input.files[0];

				// Start Upload
				const uploadParams = new FormData();
				uploadParams.append('image', image);

				const callback = (response) => {
					const path = response.data;
					const url = `${this.mediaUrlPrefix}/${path}`;
					this.storeEdit.warehouse_photo_with_owner = url;
					this.isUploadingWarehousePhotoWithOwner = false;
				};

				const errorCallback = (e) => {
					// eslint-disable-next-line
					console.log(e);
					this.isUploadingWarehousePhotoWithOwner = false;
				};

				// Upload Image
				mediaApi.uploadImage(uploadParams, callback, errorCallback);
			}
			// Reset file input
			event.target.value = null;
		},
		scrollToSection() {
			// Scroll to Section
			if (this.toQuery) {
				setTimeout(() => {
					scrollTo(this.toQuery, -70);
				}, 300);
			}
		},
		onChangeStoreStatus() {
			const modalState = !this.isStoreStatusModalVisible;
			this.toggleStoreStatusModal(modalState);
		},
		toggleStoreStatusModal(modalState = true, reset = false) {
			this.isStoreStatusModalVisible = modalState;
			if (reset) {
				this.isHolidayMode = !this.isHolidayMode;
			}
		},
		updateStoreStatus() {
			this.isUpdatingStoreStatus = true;

			const params = {
				status: this.storeStatusState,
			};

			const callback = (response) => {
				const message = response?.message || 'Success';
				this.$notify({
					group: 'app',
					title: 'Update Store Status',
					text: message,
					type: 'success',
				});
				this.isUpdatingStoreStatus = false;
				this.toggleStoreStatusModal(false);
				this.saveStoreInformation();
			};

			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					title: 'Update Store Status',
					text: message || 'Failed',
					type: 'error',
				});
				this.isUpdatingStoreStatus = false;
			};

			updateStoreStatus(params, callback, errorCallback);
		},
		// eslint-disable-next-line camelcase
		cityLabel({ province_name, city_name_with_type, subdistrict_name }) {
			// eslint-disable-next-line camelcase
			return `${province_name}, ${city_name_with_type}, ${subdistrict_name}`;
		},
		getStoreSubdistricts() {
			this.isLoadingStoreSubdistrics = true;
			const subdistrictIds = JSON.stringify(this.originIds);
			const params = {
				page: 1,
				subdistrict_ids: subdistrictIds,
				get_complete_data: true,
				keyword: this.storeSubdistrictKeyword,
			};
			const callback = (response) => {
				const subdistricts = response.data;
				this.storeSubdistricts = subdistricts;
				this.isLoadingStoreSubdistrics = false;
			};
			const errorCallback = () => {
				// const message = getAxiosErrorMessage(e);
				const message = 'City / District not Found';
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Get Subdistricts',
					text: message,
				});
				this.isLoadingStoreSubdistrics = false;
			};
			shipping.getCompleteSubdistricts(params, callback, errorCallback);
		},
		fetchStoreSubdistricts(query) {
			if (query.length >= 3) {
				this.storeSubdistrictKeyword = query;
				this.getStoreSubdistricts();
			}
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			store: 'store',
			bankAccounts: 'bankAccounts',
			isAdmin: 'isAdmin',
			isHasFeature: 'isHasFeature',
		}),
		isStoreNameValid() {
			return this.storeEdit.name !== '';
		},
		isStoreCouriersValid() {
			return this.storeEdit.shipping.couriers.length !== 0;
		},
		isStoreValid() {
			const isValid = this.isStoreNameValid && this.isStoreCouriersValid;
			return isValid;
		},
		toQuery() {
			return this.$route.query.to;
		},
		storeStatusState() {
			return this.isHolidayMode ? 'close' : 'open';
		},
		addressDetail() {
			let originAddress = {};

			if (this.selectedStoreSubdistrict != null) {
				originAddress = {
					city: this.selectedStoreSubdistrict.city,
					city_id: this.selectedStoreSubdistrict.city_id,
					city_name: this.selectedStoreSubdistrict.city_name,
					city_name_with_type: this.selectedStoreSubdistrict.city_name_with_type,
					district_id: this.selectedStoreSubdistrict.district_id,
					province_id: this.selectedStoreSubdistrict.province_id,
					province_name: this.selectedStoreSubdistrict.province_name,
					subdistrict_id: this.selectedStoreSubdistrict.subdistrict_id,
					subdistrict_name: this.selectedStoreSubdistrict.subdistrict_name,
					zip: this.selectedStoreSubdistrict.zip,
				};
			}

			return originAddress;
		},
	},
	created() {
		this.fetchProvinces();
		this.setStoreSettings();
		this.fetchWarehouses();
	},
	mounted() {
		this.checkHash();
		this.scrollToSection();
	},
	watch: {
		user() {
			this.setStoreSettings();
			this.setDefaultProvince();
		},
		provinces() {
			this.setDefaultProvince();
		},
		cities() {
			this.setDefaultCity();
		},
		subdistricts() {
			this.setDefaultSubdistrict();
		},
		shippingSubdistrict(subdistrict) {
			if (subdistrict && subdistrict.zip) {
				this.shippingZip = `${subdistrict.zip}`;
			}
		},
	},
};
</script>
